import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { onApiError } from '@/lib/api';
import { toast } from '@/ui';

/**
 * Cross-cutting API error UX inside the portal:
 *  - subscription_inactive → refresh tenant and send to /setup/plan
 *  - feature_not_in_plan   → upsell toast (pages normally gate before calling)
 *  - insufficient_credits  → toast with a top-up action
 *  - rate_limited / network_error → gentle toasts
 * Feature code still handles its own validation errors inline.
 */
export function useGlobalApiErrors() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  useEffect(
    () =>
      onApiError((e) => {
        switch (e.code) {
          case 'subscription_inactive':
            void qc.invalidateQueries({ queryKey: ['tenant-current'] });
            navigate('/setup/plan');
            break;
          case 'feature_not_in_plan':
            toast(t('errors.featureNotInPlan'), {
              action: {
                label: t('actions.upgrade'),
                onClick: () => navigate('/billing?upgrade=pro'),
              },
            });
            break;
          case 'insufficient_credits':
            toast.warning(t('errors.insufficientCredits'), {
              action: { label: t('actions.topUp'), onClick: () => navigate('/billing') },
            });
            break;
          case 'rate_limited':
            toast.warning(t('errors.rateLimited'));
            break;
          case 'network_error':
            toast.error(t('errors.networkBody'), { id: 'network' });
            break;
          default:
            break;
        }
      }),
    [navigate, qc, t],
  );
}
